import React, { useEffect, useState } from "react";
import { useRefreshRead } from "@/hooks/useRefreshRead";
import { LedgerTable } from "@/components/v3/LedgerTable";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LoadingState, EmptyState } from "@/components/v3/States";

const inputCls = "w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm";
const BLANK = { name: "", owner: "", period: "", due_date: "", validation: "", reviewer: "", status: "upcoming", evidence: "" };
const STATUSES = ["upcoming", "in_progress", "submitted", "overdue"];

export default function Regulatory() {
  const [obs, setObs] = useState(null);
  const [runs, setRuns] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [loadError, setLoadError] = useState(false);
  const reload = () => {
    Promise.all([api.get("/v3/regulatory/obligations"), api.get("/v3/cic/runs")])
      .then(([o, r]) => { setObs(o.data.obligations); setRuns(r.data.runs); setLoadError(false); }).catch(() => setLoadError(true));
  };
  useEffect(() => { reload(); }, []);
  useRefreshRead(reload);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const add = () => api.post("/v3/regulatory/obligations", form).then(() => { toast.success("Obligation added"); setForm(BLANK); reload(); }).catch(err);
  const setStatus = (o, status) => api.patch(`/v3/regulatory/obligations/${o.v3_id}`, { ...o, status }).then(reload).catch(err);
  const runCic = () => api.post("/v3/cic/run", { period }).then((r) => { toast.success(`Validated ${r.data.run.validated}/${r.data.run.accounts_expected}`); reload(); }).catch(err);
  const submitCic = (rid) => api.post(`/v3/cic/${rid}/mock-submit`).then(() => { toast.success("MOCK submitted — no bureau contacted"); reload(); }).catch(err);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-6 lg:p-8 max-w-[1300px]" data-testid="regulatory-page">
      <header className="mb-6">
        {loadError && <div role="alert" data-testid="regulatory-load-error" className="report-warning">Regulatory data could not be refreshed. No all-clear is implied. <button data-testid="regulatory-retry" onClick={reload} className="rf-link">Retry</button></div>}
        <div className="ceo-label !text-[#b3122a]">Govern · Build 9</div>
        <h1 className="ceo-fig text-3xl text-[#111111] mt-1">Regulatory & CIC — HQ framework</h1>
        <p className="text-[13px] text-slate-500 mt-1.5">
          <span className="bg-amber-50 border border-amber-300 text-amber-800 text-[11px] font-bold rounded px-1.5 py-0.5">NOT PRODUCTION FORMATS</span>
          {" "}Structural test framework only — production RBI/CIC formats require authoritative specifications and separate verification.
        </p>
      </header>

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="obligation-form">
          <div className="font-semibold text-slate-900 text-sm mb-2">New obligation</div>
          <div className="space-y-2">
            <input data-testid="ob-name" className={inputCls} placeholder="Obligation (e.g. CIC monthly submission)" value={form.name} onChange={set("name")} />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="ob-owner" className={inputCls} placeholder="Owner" value={form.owner} onChange={set("owner")} />
              <input data-testid="ob-reviewer" className={inputCls} placeholder="Reviewer" value={form.reviewer} onChange={set("reviewer")} />
              <input data-testid="ob-period" className={inputCls} placeholder="Period (YYYY-MM)" value={form.period} onChange={set("period")} />
              <input data-testid="ob-due" className={inputCls} placeholder="Due date YYYY-MM-DD" value={form.due_date} onChange={set("due_date")} />
            </div>
            <input data-testid="ob-validation" className={inputCls} placeholder="Validation required" value={form.validation} onChange={set("validation")} />
            <input data-testid="ob-evidence" className={inputCls} placeholder="Evidence link/ref (optional)" value={form.evidence} onChange={set("evidence")} />
            <button data-testid="ob-save-btn" onClick={add} className="w-full bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2">Add to registry</button>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="obligation-registry">
          <div className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900 text-sm">Regulatory calendar / obligation registry</div>
          {obs === null ? <LoadingState /> : obs.length === 0 ? <EmptyState title="No obligations yet" /> : (
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <LedgerTable data-testid="regulatory-obligations-ledger" labels={["Obligation", "Owner", "Period", "Due", "Reviewer", "Status"]} className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-200">
                  <th className="px-4 py-2">Obligation</th><th className="px-4 py-2">Owner</th><th className="px-4 py-2">Period</th>
                  <th className="px-4 py-2">Due</th><th className="px-4 py-2">Reviewer</th><th className="px-4 py-2">Status</th></tr></thead>
                <tbody>
                  {obs.map((o) => (
                    <tr key={o.v3_id} className="border-b border-slate-100" data-testid="obligation-row">
                      <td className="px-4 py-2 font-semibold text-slate-800">{o.name}<div className="text-[10px] text-slate-400 font-normal">{o.validation}</div></td>
                      <td className="px-4 py-2 text-slate-600">{o.owner}</td>
                      <td className="px-4 py-2 tabular-nums">{o.period}</td>
                      <td className="px-4 py-2 tabular-nums">{o.due_date}</td>
                      <td className="px-4 py-2 text-slate-600">{o.reviewer || "—"}</td>
                      <td className="px-4 py-2">
                        <select data-testid="obligation-status-select" value={o.status} onChange={(e) => setStatus(o, e.target.value)}
                          className="border border-slate-200 rounded px-1.5 py-1 text-[11px] font-bold uppercase">
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </LedgerTable>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm" data-testid="cic-console">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm">CIC Console (HQ)</span>
          <input data-testid="cic-period" className={`${inputCls} max-w-[130px]`} value={period} onChange={(e) => setPeriod(e.target.value)} />
          <button data-testid="cic-run-btn" onClick={runCic} className="bg-[#111111] text-white text-[12px] font-semibold rounded px-3 py-1.5">Run validation</button>
        </div>
        {runs === null ? <LoadingState /> : runs.length === 0 ? <EmptyState title="No CIC runs yet" detail="Run validation on disbursed V3 test loans." /> : runs.map((r) => (
          <div key={r.v3_id} className="px-5 py-3 border-b border-slate-100" data-testid="cic-run-row">
            <div className="flex items-center gap-4 flex-wrap text-[13px]">
              <b>{r.period}</b>
              {[["Expected", r.accounts_expected], ["Validated", r.validated], ["Errors", (r.errors || []).length],
                ["Ready", r.ready], ["Submitted", r.submitted], ["Accepted", r.accepted], ["Rejected", r.rejected]].map(([k, v]) => (
                <span key={k}><span className="text-slate-400 text-[10px] font-bold uppercase">{k} </span><b className="tabular-nums">{v}</b></span>
              ))}
              <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5">{r.status}</span>
              {r.status !== "submitted_test" && (
                <button data-testid="cic-submit-btn" onClick={() => submitCic(r.v3_id)} className="text-[11px] font-bold text-[#b3122a] border border-[#b3122a]/40 rounded px-2 py-1">Mock submit</button>
              )}
            </div>
            {(r.corrections || []).length > 0 && (
              <div className="text-[11px] text-amber-700 mt-1" data-testid="cic-corrections">Corrections: {r.corrections.map((c) => `${c.loan}: ${c.issues.join(", ")}`).join(" · ")}</div>
            )}
            {(r.errors || []).length > 0 && r.status !== "submitted_test" && (
              <div className="text-[11px] text-[#b3122a] mt-1">Errors: {r.errors.map((e) => `${e.loan}: ${e.issues.join(", ")}`).join(" · ")}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
